import type { ClinicSection, MyMembership, MySections, Permission, SectionKind } from '@clinic/contracts';
import { getContext, setContext } from 'svelte';

/**
 * The organization selected in the URL (/o/:orgId) and the caller's membership in it.
 * Used only to shape the UI; the server re-checks membership and permission on every request.
 */
export interface OrgContextValue {
  orgId: string;
  membership: MyMembership;
  can: (permission: Permission) => boolean;
  /**
   * The sections of the clinic the caller reaches (ADR-036), from the same SQL function RLS uses.
   * Empty for an organization that is not a clinic.
   */
  sections: MySections;
  reaches: (kind: SectionKind, section: ClinicSection) => boolean;
  /** Whether the clinic has a laser section at all (ADR-043); false outside a clinic. */
  laserEnabled: boolean;
}

const KEY = Symbol('org');

/** Set by the org route; the getter keeps it reactive when the membership or sections change. */
export function setOrg(get: () => OrgContextValue): void {
  setContext(KEY, get);
}

export function useOrg(): OrgContextValue {
  const get = getContext<(() => OrgContextValue) | undefined>(KEY);
  if (!get) throw new Error('useOrg must be used inside an organization route');
  // A live view: every read goes to the current value.
  return new Proxy({} as OrgContextValue, { get: (_, key) => get()[key as keyof OrgContextValue] });
}
